import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { axiosInstance } from '@/axiosConfig';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Checkbox } from "@/components/ui/checkbox"; 
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { cn } from "@/lib/utils"; 
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem } from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { CheckCircle, Check, ChevronsUpDown } from 'lucide-react';
// *** MUDANÇA: 'staticDateRange' removido do import (não é usado aqui) ***
import { useFinanceiroDropdownData, CATEGORIA_RECEBIMENTO } from '@/services/FinanceiroService'; 

// Estado inicial
const initialStateRecebimento = {
  data_emissao: new Date().toISOString().split('T')[0],
  descricao: '', 
  categoria: '',
  cliente_id: '',
  valor_base: '',
  forma_recebimento: 'PIX',
  num_parcelas: 1,
  conta_id: '',
  ja_foi_pago: false,
  recorrente: false,
  recorrencia_tipo: 'MENSAL',
  recorrencia_quantidade: 12,
  recorrencia_infinita: false,
};

// Filtro do Combobox
const comboboxFilter = (value, search) => {
  return value.toLowerCase().includes(search.toLowerCase());
};

export default function NovoRecebimentoView() {
  const navigate = useNavigate();
  const [recebimentoForm, setRecebimentoForm] = useState(initialStateRecebimento);
  const [clientePopoverOpen, setClientePopoverOpen] = useState(false);
  
  // Carrega dados dos dropdowns
  const { contasList, beneficiariosReceber, loadingDropdowns } = useFinanceiroDropdownData();

  // Define conta padrão quando a lista carregar
  useEffect(() => {
    if (contasList.length > 0) {
      setRecebimentoForm(prev => ({...prev, conta_id: contasList[0].id }));
    }
  }, [contasList]);
  
  const handleRecebimentoFormChange = (field, value) => {
    setRecebimentoForm(prev => ({ ...prev, [field]: value }));
  };
  
  const handleSubmitRecebimento = async (e) => {
    e.preventDefault();
    
    const data = {
      data_emissao: recebimentoForm.data_emissao,
      descricao: recebimentoForm.descricao,
      categoria: recebimentoForm.categoria,
      cliente_id: (recebimentoForm.cliente_id === 'avulso' || recebimentoForm.cliente_id === '') ? null : recebimentoForm.cliente_id,
      valor_base: parseFloat(recebimentoForm.valor_base),
      forma_recebimento: recebimentoForm.forma_recebimento,
      num_parcelas: parseInt(recebimentoForm.num_parcelas) || 1,
      conta_id: (recebimentoForm.conta_id === 'nenhuma' || recebimentoForm.conta_id === '') ? null : recebimentoForm.conta_id,
      ja_foi_pago: recebimentoForm.ja_foi_pago,
      recorrente: recebimentoForm.recorrente,
      recorrencia_tipo: recebimentoForm.recorrente ? recebimentoForm.recorrencia_tipo : null,
      recorrencia_quantidade: parseInt(recebimentoForm.recorrencia_quantidade) || 1,
      recorrencia_infinita: recebimentoForm.recorrencia_infinita,
    };
    
    if (!data.valor_base || data.valor_base <= 0) {
      toast.error("O 'Valor Base' deve ser maior que zero.");
      return;
    }
    if (!data.descricao || !data.categoria) {
      toast.error("Preencha 'Descrição' e 'Categoria'.");
      return;
    }
    if (data.recorrente && !data.recorrencia_tipo) {
       toast.error("Selecione o tipo de recorrência.");
       return;
    }
    
    try {
      await axiosInstance.post('/receitas', data); 
      toast.success("Recebimento(s) criado(s) com sucesso!");
      // Redireciona de volta para a lista
      navigate('/financeiro/contas-a-receber'); 
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao criar recebimento.");
    }
  };

  return (
    <div className="container mx-auto px-6 py-8">
      <div className="mb-8">
        <h1 className="text-4xl font-bold text-gray-900 mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
          Novo Recebimento
        </h1>
        <p className="text-gray-600">Crie um novo lançamento de entrada (avulso).</p>
      </div>

      <div className="bg-white rounded-xl p-6 border border-gray-200 max-w-4xl mx-auto">
        <form onSubmit={handleSubmitRecebimento} className="space-y-6">
          {/* Informações Gerais */}
          <div className="p-4 border rounded-md">
            <h4 className="font-medium mb-4">Informações Gerais</h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="md:col-span-2">
                <Label>Descrição</Label>
                <Input 
                  value={recebimentoForm.descricao} 
                  onChange={(e) => handleRecebimentoFormChange('descricao', e.target.value)} 
                  placeholder="Ex: Recebimento taxa de serviço João" 
                  required 
                />
              </div>
              <div>
                <Label>Categoria</Label>
                <Select value={recebimentoForm.categoria} onValueChange={(v) => handleRecebimentoFormChange('categoria', v)} required>
                  <SelectTrigger><SelectValue placeholder="Selecione..." /></SelectTrigger>
                  <SelectContent>
                    {CATEGORIA_RECEBIMENTO.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Data 1º Vencimento</Label>
                <Input type="date" value={recebimentoForm.data_emissao} onChange={(e) => handleRecebimentoFormChange('data_emissao', e.target.value)} required />
              </div>
              <div className="md:col-span-2">
                <Label>Receber de (Cliente)</Label>
                <Popover open={clientePopoverOpen} onOpenChange={setClientePopoverOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      role="combobox"
                      aria-expanded={clientePopoverOpen}
                      className="w-full justify-between font-normal"
                      disabled={loadingDropdowns}
                    >
                      {recebimentoForm.cliente_id
                        ? beneficiariosReceber.find((cliente) => cliente.id === recebimentoForm.cliente_id)?.nome
                        : "Selecione um cliente..."}
                      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[--radix-popover-trigger-width] max-h-[--radix-popover-content-available-height] p-0">
                    <Command filter={comboboxFilter}> 
                      <CommandInput placeholder="Buscar cliente..." />
                      <CommandEmpty>Nenhum cliente encontrado.</CommandEmpty>
                      <CommandGroup className="overflow-y-auto">
                        <CommandItem
                          key="avulso"
                          value="Nenhum (Avulso)" 
                          onSelect={() => { 
                            handleRecebimentoFormChange('cliente_id', 'avulso');
                            setClientePopoverOpen(false);
                          }}
                        >
                          <Check className={cn("mr-2 h-4 w-4", recebimentoForm.cliente_id === "avulso" ? "opacity-100" : "opacity-0")} />
                          Nenhum (Avulso)
                        </CommandItem>
                        {beneficiariosReceber.map((cliente) => (
                          <CommandItem
                            key={cliente.id}
                            value={cliente.nome} 
                            onSelect={(currentValue) => {
                              const selectedId = beneficiariosReceber.find(c => c.nome.toLowerCase() === currentValue.toLowerCase())?.id;
                              handleRecebimentoFormChange('cliente_id', selectedId || 'avulso');
                              setClientePopoverOpen(false);
                            }}
                          >
                            <Check className={cn("mr-2 h-4 w-4", recebimentoForm.cliente_id === cliente.id ? "opacity-100" : "opacity-0")} />
                            {cliente.nome}
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </Command>
                  </PopoverContent>
                </Popover>
              </div>
            </div>
          </div>
          
          {/* Recebimento */}
          <div className="p-4 border rounded-md">
            <h4 className="font-medium mb-4">Recebimento</h4>
             <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div>
                  <Label>Valor Base (R$)</Label>
                  <Input type="number" step="0.01" value={recebimentoForm.valor_base} onChange={(e) => handleRecebimentoFormChange('valor_base', e.target.value)} placeholder="100.00" required />
                  <p className="text-xs text-gray-500 mt-1">Valor de cada parcela/recorrência.</p>
                </div>
                 <div>
                  <Label>Forma de Recebimento</Label>
                  <Select value={recebimentoForm.forma_recebimento} onValueChange={(v) => handleRecebimentoFormChange('forma_recebimento', v)} required>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="PIX">Pix</SelectItem>
                      <SelectItem value="CARTAO_DEBITO">Cartão de Débito</SelectItem>
                      <SelectItem value="CARTAO_CREDITO">Cartão de Crédito</SelectItem>
                      <SelectItem value="DEPOSITO">Depósito</SelectItem>
                      <SelectItem value="BOLETO">Boleto</SelectItem>
                      <SelectItem value="OUTRO">Outro</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Conta de Destino</Label>
                  <Select value={recebimentoForm.conta_id} onValueChange={(v) => handleRecebimentoFormChange('conta_id', v)} disabled={loadingDropdowns}>
                    <SelectTrigger><SelectValue placeholder="Opcional..." /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="nenhuma">Nenhuma</SelectItem>
                      {contasList.map(c => <SelectItem key={c.id} value={c.id}>{c.nome} ({c.nome_banco})</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-end pb-2">
                  <div className="flex items-center space-x-2">
                    <Checkbox 
                      id="ja_foi_pago"
                      checked={recebimentoForm.ja_foi_pago}
                      onCheckedChange={(c) => handleRecebimentoFormChange('ja_foi_pago', c)}
                    />
                    <Label htmlFor="ja_foi_pago" className="cursor-pointer">Já foi pago?</Label>
                  </div>
                </div>
             </div>
          </div>
          
          {/* Parcelas vs Recorrência */}
          <div className="grid grid-cols-2 gap-6">
            <div className={`p-4 border rounded-md ${recebimentoForm.recorrente ? 'opacity-50 bg-gray-50' : ''}`}>
              <h4 className="font-medium mb-4">Parcelamento (Total Fixo)</h4>
              <div>
                <Label>Nº de Parcelas</Label>
                <Input 
                  type="number" 
                  value={recebimentoForm.num_parcelas} 
                  onChange={(e) => handleRecebimentoFormChange('num_parcelas', e.target.value)} 
                  disabled={recebimentoForm.recorrente}
                  min="1"
                />
                 <p className="text-xs text-gray-500 mt-1">Ex: Valor Base 100,00, 2 Parcelas = 2x 100,00.</p>
                 <p className="text-xs text-gray-500 mt-1">(Vencimentos mensais)</p>
              </div>
            </div>
            <div className="p-4 border rounded-md space-y-4">
              <div className="flex items-center space-x-2">
                <Checkbox 
                  id="recorrente"
                  checked={recebimentoForm.recorrente}
                  onCheckedChange={(c) => {
                    handleRecebimentoFormChange('recorrente', c);
                    if (c) { handleRecebimentoFormChange('num_parcelas', 1); } 
                  }}
                />
                <Label htmlFor="recorrente" className="font-medium cursor-pointer">Recorrente (Assinatura)</Label>
              </div>
              <div className={`space-y-4 ${!recebimentoForm.recorrente ? 'opacity-50' : ''}`}>
                 <div className="grid grid-cols-2 gap-4">
                   <div>
                    <Label>Tipo de Recorrência</Label>
                    <Select 
                      value={recebimentoForm.recorrencia_tipo} 
                      onValueChange={(v) => handleRecebimentoFormChange('recorrencia_tipo', v)}
                      disabled={!recebimentoForm.recorrente}
                    >
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="SEMANAL">Semanal</SelectItem>
                        <SelectItem value="QUINZENAL">Quinzenal</SelectItem>
                        <SelectItem value="MENSAL">Mensal</SelectItem>
                        <SelectItem value="SEMESTRAL">Semestral</SelectItem>
                        <SelectItem value="ANUAL">Anual</SelectItem>
                      </SelectContent>
                    </Select>
                   </div>
                   <div>
                    <Label>Qtde. Recebimentos</Label>
                    <Input 
                      type="number" 
                      value={recebimentoForm.recorrencia_quantidade} 
                      onChange={(e) => handleRecebimentoFormChange('recorrencia_quantidade', e.target.value)} 
                      disabled={!recebimentoForm.recorrente || recebimentoForm.recorrencia_infinita}
                      min="1"
                    />
                   </div>
                 </div>
                 <div className="flex items-center space-x-2">
                  <Checkbox 
                    id="infinito"
                    checked={recebimentoForm.recorrencia_infinita}
                    onCheckedChange={(c) => handleRecebimentoFormChange('recorrencia_infinita', c)}
                    disabled={!recebimentoForm.recorrente}
                  />
                  <Label htmlFor="infinito" className="cursor-pointer">Infinito</Label>
                 </div>
                 <p className="text-xs text-gray-500 mt-1">Ex: Valor Base 100,00, Recorrente Mensal = 100,00 todo mês.</p>
              </div>
            </div>
          </div>
          
          <Button type="submit" className="w-full text-lg py-6" style={{ background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)', color: '#1a202c' }}>
            <CheckCircle className="w-5 h-5 mr-2" />
            Criar Lançamento(s)
          </Button>
        </form>
      </div>
    </div>
  );
}